import { useState } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  colorAccent,
  radiusInput,
} from "src/design/tokens";
import { MenuSection } from "src/business/restaurant/menu";
import { Card } from "src/design";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  selectedCategories: string[];
  onCategoryChange: (value: string[]) => void;
  sections: MenuSection[];
}

export const MenuSearchBar = ({
  search,
  onSearchChange,
  selectedCategories,
  onCategoryChange,
  sections,
}: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const hasSelection = selectedCategories.length > 0;

  const filterLabel =
    selectedCategories.length === 0
      ? t("menu.filter.allCategories")
      : selectedCategories.length === 1
        ? selectedCategories[0]
        : t("menu.filter.nCategories", { count: selectedCategories.length });

  const toggleCategory = (section: string) => {
    if (selectedCategories.includes(section)) {
      onCategoryChange(selectedCategories.filter((c) => c !== section));
    } else {
      onCategoryChange([...selectedCategories, section]);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCategoryChange([]);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} sm>
          <TextField
            fullWidth
            size="small"
            placeholder={t("menu.searchPlaceholder")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colorTextMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: radiusInput,
                backgroundColor: colorSurface,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm="auto">


          <Button
            startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
            endIcon={
              hasSelection ? (
                <CloseIcon
                  sx={{ fontSize: 16, color: colorTextMuted }}
                  onClick={handleClear}
                  aria-label={t("menu.filter.allCategories")}
                />
              ) : undefined
            }
            onClick={(e) => setAnchorEl(e.currentTarget)}
            variant="outlined"
            sx={{
              whiteSpace: "nowrap",
              borderRadius: radiusInput,
              textTransform: "none",
              borderColor: colorBorder,
              color: colorTextPrimary,
              backgroundColor: colorSurface,
              fontSize: "0.875rem",
              px: 2,
              "&:hover": { borderColor: colorBorder, backgroundColor: "#F9FAFB" },
            }}
          >
            {filterLabel}
          </Button>
        </Grid>
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { minWidth: 220, borderRadius: "10px", mt: 0.5 } }}
      >
        <MenuItem
          onClick={() => onCategoryChange([])}
          sx={{ fontSize: "0.875rem", justifyContent: "space-between" }}
        >
          {t("menu.filter.allCategories")}
          {selectedCategories.length === 0 && (
            <CheckIcon sx={{ fontSize: 16, color: colorAccent }} />
          )}
        </MenuItem>
        {sections.length > 0 && <Divider />}
        {sections.map((s) => (
          <MenuItem
            key={s.section}
            onClick={() => toggleCategory(s.section)}
            sx={{ fontSize: "0.875rem", justifyContent: "space-between" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {s.section}
              <Typography sx={{ fontSize: "0.75rem", color: colorTextMuted }}>
                ({s.menuItems.length})
              </Typography>
            </Box>
            {selectedCategories.includes(s.section) && (
              <CheckIcon sx={{ fontSize: 16, color: colorAccent }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
};
